import React, { useEffect, useRef, useState } from 'react'
import { Box, useColorMode } from '@chakra-ui/react'
import { motion, useSpring, useMotionValue } from 'framer-motion'
import { getTokens } from '../config/colorTokens'

/**
 * CustomCursor Component
 * 
 * Provides a high-end, morphing custom cursor experience.
 * Features a central precision dot and a trailing ring that morphs 
 * to take the shape of hovered interactive elements.
 */
const CustomCursor = () => {
	const { colorMode } = useColorMode()
	const t = getTokens(colorMode)
	const [isClicked, setIsClicked] = useState(false)
	const [isVisible, setIsVisible] = useState(false)
	
	// Morphing state: stores the target styles when hovering an element
	const [targetStyle, setTargetStyle] = useState(null)
	const hoveredElementRef = useRef(null)

	const mouseX = useMotionValue(-100)
	const mouseY = useMotionValue(-100)
	const ringTargetX = useMotionValue(-100)
	const ringTargetY = useMotionValue(-100)

	// Spring configuration for the trailing/morphing ring
	const springConfig = { damping: 28, stiffness: 250, mass: 0.6 }
	const ringX = useSpring(ringTargetX, springConfig)
	const ringY = useSpring(ringTargetY, springConfig)

	useEffect(() => {
		const getInteractiveTarget = (element) => {
			if (!(element instanceof Element)) return null

			const target = element.closest('a, button, [role="button"], input, select, textarea, .interactive')
			if (target) return target

			const style = window.getComputedStyle(element)
			return style.cursor === 'pointer' ? element : null
		}

		const updateTargetStyle = (target) => {
			const rect = target.getBoundingClientRect()
			const style = window.getComputedStyle(target)

			ringTargetX.set(rect.left + rect.width / 2)
			ringTargetY.set(rect.top + rect.height / 2)
			setTargetStyle({
				width: rect.width + 8, // Slightly larger than target
				height: rect.height + 8,
				borderRadius: style.borderRadius
			})
		}

		const movePointer = (e) => {
			if (!isVisible) setIsVisible(true)
			mouseX.set(e.clientX)
			mouseY.set(e.clientY)

			if (hoveredElementRef.current) {
				updateTargetStyle(hoveredElementRef.current)
			} else {
				ringTargetX.set(e.clientX)
				ringTargetY.set(e.clientY)
			}
		}

		const handleMouseDown = () => setIsClicked(true)
		const handleMouseUp = () => setIsClicked(false)
		const handlePointerLeave = () => {
			setIsVisible(false)
			setIsClicked(false)
			hoveredElementRef.current = null
			setTargetStyle(null)
		}

		const handlePointerOver = (e) => {
			const target = getInteractiveTarget(e.target)
			if (target) {
				hoveredElementRef.current = target
				updateTargetStyle(target)
			} else {
				hoveredElementRef.current = null
				setTargetStyle(null)
			}
		}

		const handlePointerOut = (e) => {
			const nextTarget = getInteractiveTarget(e.relatedTarget)
			if (!nextTarget || nextTarget !== hoveredElementRef.current) {
				hoveredElementRef.current = nextTarget
				if (nextTarget) {
					updateTargetStyle(nextTarget)
				} else {
					setTargetStyle(null)
					ringTargetX.set(e.clientX)
					ringTargetY.set(e.clientY)
				}
			}
		}

		window.addEventListener('pointermove', movePointer)
		window.addEventListener('mousedown', handleMouseDown)
		window.addEventListener('mouseup', handleMouseUp)
		window.addEventListener('pointerover', handlePointerOver)
		window.addEventListener('pointerout', handlePointerOut)
		document.documentElement.addEventListener('pointerleave', handlePointerLeave)

		return () => {
			window.removeEventListener('pointermove', movePointer)
			window.removeEventListener('mousedown', handleMouseDown)
			window.removeEventListener('mouseup', handleMouseUp)
			window.removeEventListener('pointerover', handlePointerOver)
			window.removeEventListener('pointerout', handlePointerOut)
			document.documentElement.removeEventListener('pointerleave', handlePointerLeave)
		}
	}, [mouseX, mouseY, ringTargetX, ringTargetY, isVisible])

	if (!isVisible) return null

	const isHovered = !!targetStyle

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			right={0}
			bottom={0}
			zIndex={9999}
			pointerEvents="none"
			display={{ base: 'none', lg: 'block' }}
		>
			{/* Main Precision Dot */}
			<motion.div
				style={{
					position: 'fixed',
					left: mouseX,
					top: mouseY,
					width: 8,
					height: 8,
					backgroundColor: t.accent,
					borderRadius: '50%',
					x: '-50%',
					y: '-50%',
					boxShadow: `0 0 12px ${t.accentGlow}`,
					zIndex: 2
				}}
			/>

			{/* Morphing Outer Ring */}
			<motion.div
				animate={{
					width: isHovered ? targetStyle.width : (isClicked ? 24 : 32),
					height: isHovered ? targetStyle.height : (isClicked ? 24 : 32),
					borderRadius: isHovered ? targetStyle.borderRadius : '50%',
					opacity: isHovered ? 1 : 0.4,
					backgroundColor: isHovered ? t.accentSoft : 'transparent',
					scale: isClicked && !isHovered ? 0.8 : 1,
				}}
				style={{
					position: 'fixed',
					left: ringX,
					top: ringY,
					x: '-50%',
					y: '-50%',
					border: isHovered ? `2px solid ${t.accent}` : `1.5px solid ${t.accent}`,
					zIndex: 1
				}}
				transition={{
					type: 'spring',
					stiffness: 300,
					damping: 30,
					mass: 0.8,
					// Shape-specific transitions
					width: { duration: 0.25 },
					height: { duration: 0.25 },
					borderRadius: { duration: 0.25 }
				}}
			/>
		</Box>
	)
}

export default CustomCursor
